import { PlusIcon } from "@/assets/icons";
import ToneAlert from "@/components/ui/ToneAlert";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import type { TimeSeriesConfig } from "@/hooks/dataset-managment/useTimeSeriesConfig";

import { Button } from "equator-design-system";

import TimeAttributeRow from "./TimeAttributeRow";
import ViewHeader from "./ViewHeader";

const TEST_ID_PREFIX = "dataset-management-time-series-view";

type TimeSeriesViewProps = {
  config: TimeSeriesConfig;
  onBack: () => void;
  onCancel: () => void;
  onClose: () => void;
};

const TimeSeriesView = ({
  config,
  onBack,
  onCancel,
  onClose
}: TimeSeriesViewProps) => (
  // The whole view scrolls, not just the rows, so open dropdown menus are not clipped.
  <div className="flex flex-col max-h-[70vh] overflow-y-auto pr-1 custom-scrollbar">
    <ViewHeader
      title={DATA_SETTINGS_MESSAGE.TIMESERIES_TITLE}
      isDisabled={config.isSaving}
      onBack={onBack}
      onClose={onClose}
    />

    <span className="text-xs text-dim-gray mt-2">
      {DATA_SETTINGS_MESSAGE.TIMESERIES_DESCRIPTION}
    </span>

    <span className="text-sm font-semibold text-txtHeading mt-4">
      {DATA_SETTINGS_MESSAGE.TIMESERIES_ATTRIBUTES_TITLE}
    </span>
    <span className="text-xs text-dim-gray">
      {DATA_SETTINGS_MESSAGE.TIMESERIES_ATTRIBUTES_DESCRIPTION}
    </span>

    <div
      className="flex flex-col gap-3 mt-1 pt-2"
      data-testid={`${TEST_ID_PREFIX}-rows`}
    >
      {config.rows.map((row, index) => (
        <TimeAttributeRow
          key={row.key}
          index={index}
          attributeOptions={config.optionsByRow[index]}
          attributeId={row.attributeId}
          format={row.format}
          errorMessage={config.errorByRow[index]}
          isValid={Boolean(config.isCheckedValidByRow[index])}
          isRemoveDisabled={index === 0 && !config.canRemoveFirstRow}
          isDisabled={config.isSaving}
          onChangeAttribute={(attributeId) =>
            config.updateRow(row.key, { attributeId })
          }
          onChangeFormat={(format) => config.updateRow(row.key, { format })}
          onRemove={() => config.removeRow(row.key)}
        />
      ))}
    </div>

    {config.isNumericMissing && (
      <ToneAlert
        tone="error"
        title={DATA_SETTINGS_MESSAGE.TIMESERIES_NUMERIC_REQUIRED_TITLE}
        className="mt-4"
        data-testid={`${TEST_ID_PREFIX}-numeric-alert`}
      >
        {DATA_SETTINGS_MESSAGE.TIMESERIES_NUMERIC_REQUIRED_DESCRIPTION}
      </ToneAlert>
    )}

    <button
      type="button"
      className="flex items-center justify-center gap-1 mt-4 bg-transparent border-0 text-sm font-semibold text-primary-teal disabled:opacity-40 disabled:cursor-not-allowed"
      disabled={!config.canAddRow || config.isSaving}
      onClick={config.addRow}
      data-testid={`${TEST_ID_PREFIX}-add-button`}
    >
      <PlusIcon className="w-4 h-4" />
      {DATA_SETTINGS_MESSAGE.TIMESERIES_ADD_ATTRIBUTE}
    </button>

    <div className="flex gap-2 w-full pt-4">
      <Button
        className="h-[40px] w-full btn-secondary text-sm !font-bold"
        disabled={config.isSaving}
        onClick={onCancel}
        data-testid={`${TEST_ID_PREFIX}-cancel-button`}
      >
        <span>{DATA_SETTINGS_MESSAGE.TIMESERIES_CANCEL}</span>
      </Button>
      <Button
        className="h-[40px] w-full btn-primary text-sm !font-bold"
        disabled={!config.canSave}
        onClick={config.save}
        data-testid={`${TEST_ID_PREFIX}-save-button`}
      >
        <span>
          {config.isSaving
            ? DATA_SETTINGS_MESSAGE.TIMESERIES_SAVING
            : DATA_SETTINGS_MESSAGE.TIMESERIES_SAVE}
        </span>
      </Button>
    </div>
  </div>
);

export default TimeSeriesView;
